import type { FC } from 'react'
import styled from '@emotion/styled'
import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'

import ButtonText from '@segment/matcha/components/ButtonText'

import { ChevronDownIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import EmailCaptureForm from '@segment/templates/Forms/EmailCaptureForm'

import useScrollToAnchor from '@segment/utils/useScrollToAnchor/useScrollToAnchor'

import heroPng from './assets/Hero.png'
import heroWebp from './assets/Hero.webp'

const copy = {
  overline: `A Guide to Customer-Centric Tools & Technologies`,
  heading: `The Customer Engagement Stack`,
  linkText: `Continue reading`,
  formDescription: `Want to save this as a resource? Download the ebook below.`,
  description: `Welcome to the second annual edition of 'The Customer Engagement Stack', our yearly exploration of the leading tools and tactics businesses around the world are using to elevate customer interactions.`,
}

const button = {
  text: `Download the ebook`,
  url: `https://segment.com/pdfs/ebook-the-customer-engagement-stack-2024.pdf`,
}
const Hero: FC = () => {
  const scrollToIntro = useScrollToAnchor(`introduction`, -80)
  return (
    <Wrapper id="download-the-ebook">
      <StyledHeroBase variant="2Col">
        <HeroCopy>
          <CopyTextBase>
            <CopyText.Overline>{copy.overline}</CopyText.Overline>
            <CopyText.Heading variant="h1" tag="h1">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description variant="pLarge" tag="p">
              {copy.description}
            </CopyText.Description>
            <StyledButtonText iconEnd={ChevronDownIcon} onClick={() => scrollToIntro()}>
              {copy.linkText}
            </StyledButtonText>
          </CopyTextBase>
        </HeroCopy>

        <HeroContents>
          <ImageWrapper>
            <picture>
              <source type="image/webp" srcSet={`${heroWebp}`} />
              <HeroRecipe.Image
                width="1586"
                height="892"
                src={heroPng}
                alt="Illustration: Customer Engagement Stack"
                loading="lazy"
              />
            </picture>
          </ImageWrapper>
          <StyledCopyTextBase>
            <CopyText.Description variant="pLarge" tag="p">
              {copy.formDescription}
            </CopyText.Description>
          </StyledCopyTextBase>
          <StyledHeroRecipeForm id="cnt-customer-eng-stack-2024-form">
            <EmailCaptureForm
              additionalTrackEvent="Content Downloaded"
              resourceType="report"
              buttonText={button.text}
              successButtonText={button.text}
              buttonUrl={button.url}
              successMessageAppearance="twilioLight"
              customFormIdentifier="cnt-customer-eng-stack-2024"
              enableJobTitleField={true}
            />
          </StyledHeroRecipeForm>
        </HeroContents>
      </StyledHeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

const StyledHeroBase = styled(HeroBase)`
  ${HeroRecipe.Wrapper} {
    justify-content: unset;
  }
`

const contentAlignmentStyles = (theme: Theme) => css`
  ${theme.breakpoints.down(`md`)} {
    margin-left: auto;
    margin-right: auto;
  }
`

const StyledCopyTextBase = styled(CopyTextBase)`
  ${({ theme }) => theme.breakpoints.between(`sm`, `md`)} {
    text-align: center;
  }
  ${({ theme }) => contentAlignmentStyles(theme)};
`

const ImageWrapper = styled.div`
  max-width: 520px;
  padding-bottom: 40px;
  ${({ theme }) => contentAlignmentStyles(theme)};
`

const StyledButtonText = styled(ButtonText)`
  padding-top: 18px;
`

const heroColumnStyles = (theme: Theme) => css`
  margin: 0 auto;
  ${theme.breakpoints.up(`lg`)} {
    max-width: unset;
  }
`

const HeroCopy = styled(HeroRecipe.Copy)`
  ${({ theme }) => heroColumnStyles(theme)};
  align-self: flex-start;
  text-align: left;
`

const HeroContents = styled(HeroRecipe.Contents)`
  ${({ theme }) => heroColumnStyles(theme)};
  justify-items: unset;
`

const StyledHeroRecipeForm = styled(HeroRecipe.Form)`
  max-width: 515px;
  ${({ theme }) => contentAlignmentStyles(theme)};
`

export default Hero
